import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {t} from 'i18next';
import {useDispatch, useSelector} from 'react-redux';

import {
  POPUP_BUTTON_STYLE,
  PROJECT_LIST_SEARCH_ENABLE_COUNT,
  MODAL_PROJECT_DROP_DOWN_ID,
} from '../../../../constants/constants';
import AurClientDropdown from '../../../client-management/aur-client-dropdown/AurClientDropdown';
import AurPopup from '../../../common/aur-popup/AurPopup';
import {RootState} from '../../../../redux/store';
import {getEcaProjectsList} from '../../../../api/ClientManagementService';
import {
  setModalDropDownSelectedItem,
  setModalStatus,
} from '../../../../redux/slices/invoiceModalSlice';
import {
  setProjectName,
  setProjectNlpSearch,
} from '../../../../redux/slices/reportsSlice';
import usePermissionsByProjectId from '../../../../hooks/permissions/usePermissionsByProjectId';
import {IProjectSwitchItem} from '../../../../types/project-type/projectDetailsType';
import {unionBy} from 'lodash';
import AurLoadingIndicator from '../../../common/aur-loading-indicator/AurLoadingIndictor';

export const PROJECT_PAGE_SIZE = 25;

interface Props {
  title?: string;
  heading?: string;
  headingClass?: string;
  initialSelectedProjectId?: string;
  className?: string;
  closeIcon?: ReactElement;
}
const ProjectSwitcherModal: React.FC<Props> = ({
  title,
  heading,
  headingClass,
  initialSelectedProjectId,
  className,
  closeIcon,
}) => {
  const dispatch = useDispatch();
  const projects = useSelector(
    (state: RootState) => state.clientReducer[MODAL_PROJECT_DROP_DOWN_ID]
  );

  const modalStatus = useSelector(
    (state: RootState) => state.invoiceModal.modalIsOpen
  );

  const clientId = useSelector(
    (state: RootState) => state.selectedClient
  ).clientId;

  const selectedProject = useSelector(
    (state: RootState) => state.invoiceModal.modalDropDownSelectedItem
  );

  const nlpSearchEnabled = useSelector(
    (state: RootState) => state.reports.nlpSearchEnabled
  );

  const [viewInvoicesProjectId, setViewInvoicesProjectId] = useState<
    string | null
  >(initialSelectedProjectId ?? selectedProject.id);
  const [dropDownProjects, setDropDownProjects] = useState<
    IProjectSwitchItem[]
  >([]);
  const [initialProjectListLoading, setInitialProjectListLoading] =
    useState<boolean>(true);
  const [projectListLoading, setProjectListLoading] = useState<boolean>(false);
  const [projectListSearchEnable, setProjectListSearchEnable] =
    useState<boolean>(false);
  const pickedProjectFromDropdown = useRef<IProjectSwitchItem>();
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);

  const {getPermissionsByClientIdProjectId} = usePermissionsByProjectId();

  const prevFetchParams = useRef<{
    searchInput: string | undefined;
    page: number;
    reset: boolean;
  }>({searchInput: undefined, page: 0, reset: false});
  const currentSearchTerm = useRef<string | undefined>(undefined);

  const selectedProjectFromRedux = useMemo(() => {
    return {
      projectId: selectedProject.id,
      projectCode: selectedProject.code ?? '',
      projectName: selectedProject.name,
      nlpSearchEnabled: !!nlpSearchEnabled,
      projectStatus: 'Active',
      projectAlphaCode: selectedProject.alphaCode ?? '',
      domainCountryCode: selectedProject.domainCountryCode ?? '',
      ecaEnabled: true,
    };
  }, [
    nlpSearchEnabled,
    selectedProject.alphaCode,
    selectedProject.code,
    selectedProject.domainCountryCode,
    selectedProject.id,
    selectedProject.name,
  ]);

  // Fetch eca project list data from API
  const fetchEcaProjectList = async (
    searchInput?: string,
    page = 1,
    reset = false,
    initial = false
  ) => {
    if (
      prevFetchParams.current.searchInput === searchInput &&
      prevFetchParams.current.page === page &&
      prevFetchParams.current.reset === reset
    ) {
      setInitialProjectListLoading(false);
      return;
    }
    prevFetchParams.current = {searchInput, page, reset};

    if (page === 1) {
      setProjectListLoading(true);
    } else {
      setIsLoadingMore(true);
    }

    try {
      let params =
        `?clientId=${clientId}` +
        `&status=Active` +
        `&PageSize=${PROJECT_PAGE_SIZE}` +
        `&PageNumber=${page}`;

      if (searchInput) params += `&SearchTerm=${searchInput}`;

      const result = await getEcaProjectsList(params);
      const fetchedProjects: IProjectSwitchItem[] = result?.data?.data ?? [];
      setHasMore(fetchedProjects.length === PROJECT_PAGE_SIZE);
      if (
        !projectListSearchEnable &&
        fetchedProjects.length >= PROJECT_LIST_SEARCH_ENABLE_COUNT
      ) {
        setProjectListSearchEnable(true);
      }
      const newData = reset
        ? fetchedProjects
        : unionBy([...dropDownProjects, ...fetchedProjects], 'projectId');

      if (initial && page === 1) {
        const selectedOptionExitIndex = newData.findIndex(
          (item) => item.projectId === viewInvoicesProjectId
        );
        if (selectedOptionExitIndex === -1 && viewInvoicesProjectId) {
          newData.unshift(selectedProjectFromRedux);
          if (newData.length > PROJECT_PAGE_SIZE) {
            newData.pop();
          }
        } else {
          newData.splice(
            0,
            1,
            newData.splice(selectedOptionExitIndex, 1, newData[0])[0]
          );
        }
      }
      if (
        initialSelectedProjectId &&
        pickedProjectFromDropdown.current === undefined
      ) {
        pickedProjectFromDropdown.current = newData.find(
          (item) => item.projectId === initialSelectedProjectId
        );
      }

      setDropDownProjects(newData);
    } catch (e) {
      console.error('Error fetching ECA projects', e);
    } finally {
      setProjectListLoading(false);
      setIsLoadingMore(false);
      setInitialProjectListLoading(false);
    }
  };

  useEffect(() => {
    if (modalStatus) {
      setInitialProjectListLoading(true);
      fetchEcaProjectList('', 1, true, true);
    }
  }, [modalStatus]);

  const loadMoreProjects = () => {
    if (!hasMore || isLoadingMore) return;
    const nextPage = pageNumber + 1;
    setPageNumber(nextPage);
    fetchEcaProjectList(currentSearchTerm.current, nextPage);
  };

  const handleProjectListSearchInput = useCallback(
    (input: string) => {
      currentSearchTerm.current = input;
      setPageNumber(1);
      fetchEcaProjectList(input, 1, true);
    },
    [viewInvoicesProjectId, fetchEcaProjectList]
  );

  const onProjectPick = (projectId: string) => {
    pickedProjectFromDropdown.current = dropDownProjects.find(
      (item) => item.projectId === projectId
    );
  };

  const modalContinueHandler = () => {
    if (pickedProjectFromDropdown.current) {
       // Removed the condtion for modal opens (hostCountry !== projectCountryCode)
        getPermissionsByClientIdProjectId(
          pickedProjectFromDropdown?.current?.projectId
        );
        dispatch(
          setModalDropDownSelectedItem(
            projects.selectedOption && projects.selectedOptionLable
              ? {
                  id: projects.selectedOption,
                  name: projects.selectedOptionLable,
                }
              : {id: '', name: ''}
          )
        );
        dispatch(setModalStatus(false));
        dispatch(
          setProjectName({
            projectId: pickedProjectFromDropdown?.current?.projectId,
            projectName: pickedProjectFromDropdown?.current?.projectName,
          })
        );
        dispatch(
          setProjectNlpSearch(
            pickedProjectFromDropdown?.current?.nlpSearchEnabled || false
          )
        );
        setViewInvoicesProjectId(pickedProjectFromDropdown?.current?.projectId);
        currentSearchTerm.current = undefined;
        setPageNumber(1);
        prevFetchParams.current = {
          searchInput: undefined,
          page: 0,
          reset: false,
        };
        fetchEcaProjectList();
    }
  };

  const modalCancelHandler = () => {
    currentSearchTerm.current = undefined;
    pickedProjectFromDropdown.current = dropDownProjects.find(
      (item) => item.projectId === viewInvoicesProjectId
    );
    dispatch(setModalStatus(false));
  };

  return (
    <>
      <AurPopup
        open={modalStatus}
        onOk={modalContinueHandler}
        className={`find-invoice-popup ${className ?? ''}`}
        onCancel={modalCancelHandler}
        onClose={modalCancelHandler}
        buttonStyle={POPUP_BUTTON_STYLE.SIDEBYSIDE}
        popupHeading={
          title ?? t('viewInvoices.projectSelectPopUp.headingIndexes')
        }
        showPopupHeading={true}
        contentType="element"
        labelActionCancel={t('buttons.cancel')}
        labelActionOk={t('buttons.continue')}
        showCloseIcon={true}
        showPopupTitle={true}
        popupTitle={
          heading ?? t('viewInvoices.projectSelectPopUp.titleIndexes')
        }
        titleCustomClass={headingClass}
        continueButtonState={projects?.selectedOption ? false : true}
        exitButtonHint={t('viewInvoices.projectSelectPopUp.cancelHint')}
        continueButtonHint={t('viewInvoices.projectSelectPopUp.continueHint')}
        cancelButtonTitle=""
        closeIcon={closeIcon}
        showOkOption={!initialProjectListLoading}
        showCancelOption={!initialProjectListLoading}
        popupContent={
          initialProjectListLoading ? (
            <AurLoadingIndicator
              className="project-switch-modal-loader"
              size={32}
              show={initialProjectListLoading}
            />
          ) : (
            <AurClientDropdown
              buttonOrTextField={'button'}
              data={dropDownProjects}
              buttonLabel={t('viewInvoices.projectSelectPopUp.projectName')}
              dropDownID={MODAL_PROJECT_DROP_DOWN_ID}
              idKey="projectId"
              displayKey="projectName"
              buttonBorderHighlight={true}
              selectDefaultFirstItem={false}
              initialSelectedOptionId={
                pickedProjectFromDropdown.current?.projectId ??
                selectedProject.id
              }
              onSearchInput={handleProjectListSearchInput}
              searchReqLoading={projectListLoading}
              // disableDropDownForSingleItem
              enableSearch={projectListSearchEnable}
              onOptionPick={onProjectPick}
              hintToViewOnSelection={t('viewInvoices.invoiceBreadCrumb')}
              dataTestId="project-dropdown-button"
              onLoadMore={loadMoreProjects}
              isLoadingMore={isLoadingMore}
              isSortedAlphabetically={false}
            />
          )
        }
      />
    </>
  );
};

export default ProjectSwitcherModal;
